import { FunctionComponent, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { IS_PRODUCTION } from '../../../env';
import { storyApi } from '../../../services';
import {
  ConfirmationModal,
  getChannelDomain,
  getOrganisationDomain,
  getWebAppUrl,
  useAppDispatch,
  useSupabase,
} from '../../../shared';
import { deleteStory } from '../../../slices';
import { INTEGRATIONS_TABLE, STEADY_FEEDS_TABLE } from '../../Integrations';

interface DeleteStoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  storyId: number;
  navigateUrl?: string;
}

export const DeleteStoryModal: FunctionComponent<DeleteStoryModalProps> = memo(
  ({ isOpen, onClose, storyId, navigateUrl }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const [storyDeleteTrigger] = storyApi.endpoints.storyDelete.useMutation();

    const { supabase } = useSupabase();

    const [loading, setLoading] = useState(false);

    const [showDeleteError, setShowDeleteError] = useState(false);

    const storyDeleteHandler = useCallback(async () => {
      setLoading(true);
      try {
        const deleted = await storyDeleteTrigger({ storyId }).unwrap();

        if (!deleted) {
          setShowDeleteError(true);
          return;
        }

        if (supabase) {
          await supabase.from(INTEGRATIONS_TABLE).delete().eq('mix_id', storyId);

          if (getOrganisationDomain() === 'steady') {
            await supabase.from(STEADY_FEEDS_TABLE).delete().eq('default_mix_id', storyId);
          }
        }

        if (navigateUrl) {
          window.location.replace(
            IS_PRODUCTION ? `${getWebAppUrl(getChannelDomain())}/${navigateUrl}` : navigateUrl
          );
          return;
        }

        dispatch(deleteStory({ storyId }));
        toast(t('storyActions.story-deleted'));
      } catch (_) {
        toast(t('deleteConfirmation.error-message'));
      } finally {
        onClose();
        setLoading(false);
      }
    }, [dispatch, navigateUrl, onClose, storyDeleteTrigger, storyId, supabase, t]);

    return (
      <ConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        acceptLabel={t('common.delete')}
        onAccept={storyDeleteHandler}
        title={t('deleteConfirmationStory.title')}
        subTitle={t('deleteConfirmationStory.sub-title')}
        keepOpened={loading}
        loading={loading}
        danger
        {...(showDeleteError && { error: t('deleteConfirmation.error-message') })}
      />
    );
  }
);
